/**
 * Renders the assistant's Markdown answers (bold, headings, lists, tables,
 * code, links) instead of showing the raw asterisks and hashes.
 * `react-markdown` builds React elements and never injects raw HTML
 * (`rehype-raw` is deliberately not used), so model output cannot smuggle
 * markup into the page. Links to the app's own pages (relative URLs the
 * assistant writes for quotes, templates, CRM entries) navigate in place;
 * other links open in a new tab. Markdown images are rendered as their alt
 * text only, never as an `<img>`: an image URL in a model answer is never
 * fetched, so it cannot leak data through its query string.
 */
import { Box, Link, Typography } from "@mui/material";
import React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { Link as RouterLink } from "react-router-dom";
import remarkGfm from "remark-gfm";

const heading =
  (variant: "subtitle1" | "subtitle2") =>
  ({ children }: { children?: React.ReactNode }) => (
    <Typography
      variant={variant}
      sx={{ fontWeight: 600, mt: 1.25, mb: 0.5, "&:first-of-type": { mt: 0 } }}
    >
      {children}
    </Typography>
  );

const linkSx = {
  fontWeight: 600,
  color: "primary.main",
  textDecoration: "underline",
  textDecorationThickness: "1px",
  textUnderlineOffset: "2px",
  "&:hover": { textDecorationThickness: "2px", bgcolor: "action.hover" },
  "& code": { color: "inherit", bgcolor: "transparent", fontWeight: 600 },
};

const components: Components = {
  h1: heading("subtitle1"),
  h2: heading("subtitle1"),
  h3: heading("subtitle2"),
  h4: heading("subtitle2"),
  h5: heading("subtitle2"),
  h6: heading("subtitle2"),
  p: ({ children }) => (
    <Typography variant="body2" sx={{ my: 0.75 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 2.5, my: 0.5 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body2" sx={{ my: 0.25 }}>
      {children}
    </Typography>
  ),
  // Links must read as clickable at a glance: bold, always underlined, with
  // an arrow (→ opens in the app, ↗ a new tab); code inside takes the link
  // colour instead of the grey code style.
  a: ({ href, children }) => {
    // A protocol-relative URL ("//host/...") starts with "/" too, but is
    // external — the router would resolve it against this app's own host.
    const internal = Boolean(
      href && href.startsWith("/") && !href.startsWith("//")
    );
    const content = (
      <>
        {children}
        <Box component="span" aria-hidden sx={{ ml: 0.25, fontSize: "0.85em" }}>
          {internal ? "→" : "↗"}
        </Box>
      </>
    );
    return internal && href ? (
      <Link component={RouterLink} to={href} sx={linkSx}>
        {content}
      </Link>
    ) : (
      <Link href={href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
        {content}
      </Link>
    );
  },
  // Never render an `<img>` (its URL would be fetched): show the alt text as
  // plain text, or nothing when it is empty. Inside a link the link stays.
  img: ({ alt }) => (alt ? <>{alt}</> : null),
  code: ({ children }) => (
    <Box
      component="code"
      sx={{
        fontFamily: "monospace",
        fontSize: "0.85em",
        bgcolor: "action.hover",
        px: 0.5,
        borderRadius: 0.5,
      }}
    >
      {children}
    </Box>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        my: 1,
        p: 1,
        bgcolor: "action.hover",
        borderRadius: 1,
        overflowX: "auto",
        "& code": { bgcolor: "transparent", p: 0 },
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{ border: 0, borderTop: 1, borderColor: "divider", my: 1 }}
    />
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        m: 0,
        my: 1,
        pl: 1.5,
        borderLeft: 3,
        borderColor: "divider",
        color: "text.secondary",
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: "auto", my: 1 }}>
      <Box
        component="table"
        sx={{
          borderCollapse: "collapse",
          fontSize: "0.8125rem",
          width: "100%",
          "& th, & td": {
            border: 1,
            borderColor: "divider",
            px: 0.75,
            py: 0.5,
            textAlign: "left",
            verticalAlign: "top",
          },
          "& th": { bgcolor: "action.hover", fontWeight: 600 },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
};

export function AgentMarkdown({ content }: { content: string }) {
  return (
    <Box sx={{ "& > :first-child": { mt: 0 }, "& > :last-child": { mb: 0 } }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </Box>
  );
}
